import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Inject,
  Input,
  OnInit,
  Output,
  Signal,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AdditionalParams } from '@irian-ro/abstract-resource';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map, Subject, startWith, switchMap, tap } from 'rxjs';
import { SelectableResourceService } from '@well/common-libs/selectable-resource-management/util';
import { CacheResourceStore } from './cache-resource.store';
import { FloatLabel } from 'primeng/floatlabel';
import {
  BsAutocompleteComponent,
  BsItemDirective,
  BsFooterDirective,
  BsFloatLabelCompatDirective
} from 'iriro-frontend-libs/bs-component-store';

@Component({
  selector: 'well-resource-id-dropdown-standalone',
  templateUrl: './resource-id-dropdown-standalone.component.html',
  styleUrls: ['./resource-id-dropdown-standalone.component.scss'],
  imports: [
    FormsModule,
    TranslateModule,
    ReactiveFormsModule,
    NgClass,
    FloatLabel,
    BsAutocompleteComponent,
    BsItemDirective,
    BsFooterDirective,
    BsFloatLabelCompatDirective
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ResourceIdDropdownStandaloneComponent),
      multi: true,
    }
  ],
  standalone: true,
})
export class ResourceIdDropdownStandaloneComponent implements ControlValueAccessor, OnInit {
  @Input() resourceType: (new () => any) | undefined = undefined;
  @Input() placeholder: string | null | undefined = null;
  @Input() idField = 'uuid';
  @Input() labelField = 'label';
  @Input() filterFields: string[] | null = null;
  @Input() valueField: string | null = null;
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() defaultFilters: any;
  @Input() additionalParams: Array<AdditionalParams> = [];
  @Input() showClear = false;
  @Input() invalid = false;
  @Input() dirty = false;
  @Input() translationPath: string | null = null;
  @Input() translationField: string | null = null;

  private searchQuery$ = new Subject<string>();
  resources: Signal<any[]>;
  selectedResource: any | undefined = undefined;
  resourceService: SelectableResourceService<any> | undefined = undefined;
  bsLoading = signal(false);

  @Output() selectResource: Subject<any> = new Subject<any>();
  @Output() clear = new EventEmitter<void>();

  private onChange: (selectedResource: any) => void = (selectedResource: any) => {
    return;
  };
  private onTouch: () => void = () => {
    return;
  };

  store = inject(CacheResourceStore);
  private translateService = inject(TranslateService);

  constructor(
    @Inject(SelectableResourceService) private selectableResourceServices: SelectableResourceService<any>[]
  ) {
    this.resources = toSignal(
      this.searchQuery$.pipe(
        startWith(''),
        tap(() => this.bsLoading.set(true)),
        switchMap(query => {
          if (!this.resourceService) {
            this.bsLoading.set(false);
            return [[]];
          }
          const filters = this.defaultFilters || {};
          if (this.filterFields) {
            this.filterFields.forEach((filterField) => {
              filters[filterField] = {
                value: query,
                matchMode: 'contains',
              };
            });
          } else {
            filters[this.labelField] = {
              value: query,
              matchMode: 'contains',
            };
          }
          return this.fetchResourcesInternal(filters).pipe(
            tap(() => this.bsLoading.set(false)),
            map(resources => resources.data?.length > 0 ? [...resources.data] : [])
          );
        })
      ),
      { initialValue: [] }
    );
  }

  ngOnInit() {
    this.resourceService = this.selectableResourceServices.find((selectableResourceService) => {
      return selectableResourceService.getType() === this.resourceType;
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  onBlur() {
    if (this.onTouch) {
      this.onTouch();
    }
  }

  writeValue(resource: any): void {
    if (resource) {
      this.getOne(this.valueField ? resource : resource[this.idField]).subscribe((resource) => {
        if (resource && resource.data?.length > 0) {
          this.selectedResource = { ...resource.data[0] };
          this.onChange(this.valueField ? this.selectedResource[this.valueField] : this.selectedResource);
        }
      });
    } else {
      this.selectedResource = null;
    }
  }

  onSelect(): void {
    this.onChange(this.valueField ? this.selectedResource[this.valueField] : this.selectedResource);
    this.selectResource.next(this.selectedResource);
  }

  private fetchResourcesInternal(filters: any) {
    if (!this.resourceService) {
      throw new Error('ResourceService is not initialized');
    }

    const query = JSON.stringify({ filters });

    return this.store.getCachedResources(
      query,
      this.additionalParams,
      this.resourceType?.name ?? '',
      this.resourceService.getCacheable() ?? false,
      this.resourceService.getCacheDuration() ?? 0,
      this.resourceService.findAll.bind(this.resourceService)
    );
  }

  onQueryChange(query: string) {
    this.searchQuery$.next(query);
  }

  private getOne(uuid: string) {
    const filters: { [key: string]: object } = {};
    filters[this.idField] = {
      value: uuid,
      matchMode: 'equals',
    };

    return this.fetchResourcesInternal(filters);
  }

  onClear() {
    this.selectedResource = null;
    this.onChange(this.selectedResource);
    this.selectResource.next(this.selectedResource);
    this.clear.emit();
  }

  getOptionLabel = (item: any): string => {
    if (item == null) {
      return '';
    }
    if (this.translationPath) {
      const field = this.translationField ?? this.labelField;
      const translationKey = `${this.translationPath}.${item[field]}`;
      const translation = this.translateService.instant(translationKey);

      return translation !== translationKey ? translation : item[field];
    }
    return item[this.labelField] ?? '';
  };

  compareByIdField = (a: any, b: any): boolean => {
    return a && b && a[this.idField] === b[this.idField];
  };
}
